
import React, {  useCallback }from 'react';
import styled from 'styled-components';

import { AgGridReact } from 'ag-grid-react';
import { type ColDef, themeAlpine } from 'ag-grid-community';
import '@process-advisor/process-miner/shared/gridStyles.css'

import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { openFindingForContext, setFindingContextFromGridRow } from '@process-advisor/process-miner/shared/findingContext';
import '@process-advisor/process-miner/shared/gridStyles.css'
import { openProgressModal } from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal } from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';

const Wrapper = styled.div<{ height: string }>`
    height: ${({ height }) => height};
    width: 100%;
    margin-bottom: 5px;
`;

type SingleRow = {
    id: number;
    [key: string]: any
}

type Props = {
    rows: SingleRow[];
    columns: ColDef[];
    height: string;
    mode: any;
    setApplyEnabled: (value: boolean) => void;
}

export const DataGrid: React.FC<Props> = ({
    rows,
    columns,
    height,
    mode,
    setApplyEnabled,
}) => {
    const sleep = ms => new Promise(r => setTimeout(r, ms));
   
    const onSelectionChanged = (params) => {
        apiProvider.setVariantFilterSelectedData({
            selection: params.api.getSelectedRows(),
        });
        setApplyEnabled(false);
    };

    const onFirstDataRendered = (params) => {
        if (apiProvider.getVariantFilterGridState()!= null ) params.api.applyColumnState({state: apiProvider.getVariantFilterGridState()});
    };
    
    const onColumnVisible  = (params) => {
        onSaveGridColumnState(params)
    }
    const onColumnResized  = (params) => {
        onSaveGridColumnState(params)
    }
    const onSortChanged  = (params) => {
        onSaveGridColumnState(params)
    }
    const onFilterChanged  = (params) => {
        onSaveGridColumnState(params)
    }

    const onSaveGridColumnState = (params) => {
        let columnState = params.api.getColumnState();
        apiProvider.saveVariantFilterGridState({columnState})
    }

    // default selection to currently selected variants

    const menuLabel = (symbol, label) => label;
    const menuIcon = (symbol) => `<span class="pa-ag-menu-icon">${symbol}</span>`;

    const getContextMenuItems = useCallback((params) => {
        const saveFindingMenuItem = {
            name: menuLabel('✚', 'Save Finding'), icon: menuIcon('✚'),
            action: function () {
                openFindingForContext(apiProvider, { source: 'Explorer - Variants', objectType: 'Variant', row: params?.node?.data || {} });
            },
        };
        var result = [
            saveFindingMenuItem,
            'separator',
            {
                name: menuLabel('◆', 'Insights'), icon: menuIcon('◆'),
                subMenu: [
                    {
                        // custom item
                        name: menuLabel('●', 'Clusters'), icon: menuIcon('●'),   
                        action: async function () {
                            openProgressModal({status: 'Processing Clusters'})
                            await sleep(200) // needed to display Progress
                            await apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'variant', variant: params.node.data.variant_id, activity: 0, from: 0, to: 0})
                            apiProvider.setDiagramCluster({source: 'variant'})
                            closeProgressModal()
                        },  
                    },  
                    {
                        // custom item
                        name: menuLabel('↗', 'Influencers'), icon: menuIcon('↗'),
                        action: async function () {
                            openProgressModal({status: 'Processing Influencer Insights'})
                            await sleep(200) // needed to display Progress
                            await apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'variant', variant: params.node.data.variant_id, activity: 0, from: 0, to: 0})
                            apiProvider.setDiagramInfluenceAnalysis({source: 'variant'})
                            closeProgressModal()
                        },
                    },     
                    {
                        // custom item
                        name: menuLabel('▦', 'Decompose'), icon: menuIcon('▦'),
                        action: function () {
                            apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'variant', variant: params.node.data.variant_id, activity: 0, from: 0, to: 0})
                            apiProvider.setDiagramDecompositionTree({source: 'variant'})
                        },
                    },
                    {
                        name: menuLabel('◷', 'Activity Analysis'), icon: menuIcon('◷'),
                        action: function () {
                            apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'variant', variant: params.node.data.variant_id, activity: 0, from: 0, to: 0})
                            apiProvider.setTimeInActivitySource({source: 'variant'})
                            apiProvider.setAction_Time({})
                        },
                    },
                    {
                        name: menuLabel('▥', 'Histogram'), icon: menuIcon('▥'),
                        action: function () {
                            apiProvider.generateHistogramArray({variantPaneVariants: false, type: 'variant-tasks', variant: params.node.data.variant_id, activity: 0, from: 0, to: 0})
                            apiProvider.histogram({})
                        },
                    },
                ]
            },

            {
                name: apiProvider.getEntityUpper() + 's',
                subMenu: [
                    {
                        name: menuLabel('≡', 'Detail'), icon: menuIcon('≡'),
                        action: function () {
                            apiProvider.setVariantDetailProps({variant: params.node.data.variant_id})
                            apiProvider.taskDetail({})
                        },
                    },
                    {
                        // custom item
                        name: menuLabel('↻', 'History'), icon: menuIcon('↻'),   
                        action: function () {
                            apiProvider.processAnalytics({variant: params.node.data.variant_id.toString(), object_type: 'Variant', object_name: params.node.data.variant, series_name: apiProvider.getEntityUpper() + 's'})
                        },
                    },    
                    {
                        name: menuLabel('⟳', 'LifeCycle'), icon: menuIcon('⟳'),
                        action: function () {
                            apiProvider.generateTasksFilteredArray({variantPaneVariants: false, type: 'variant', variant: params.node.data.variant_id, activity: 0, from: 0, to: 0})
                            apiProvider.setTasksVisualizationSource({source: 'variant'})
                            apiProvider.tasksVisualization()
                            apiProvider.taskLifecycle({})
                        },
                    },

                ],
            },       
            {
                name: menuLabel('◇', 'Events'), icon: menuIcon('◇'),
                subMenu: [
                    {
                        name: menuLabel('≡', 'Detail'), icon: menuIcon('≡'),
                        action: function () {
                            apiProvider.setVariantDetailProps({variant: params.node.data.variant_id })
                            apiProvider.eventDetail({})
                        },
                    },
                    {
                        name: menuLabel('▥', 'Histogram'), icon: menuIcon('▥'),
                        action: function () {
                            apiProvider.generateHistogramArray({variantPaneVariants: false, type: 'variant-events', variant: params.node.data.variant_id, activity: 0, from: 0, to: 0})
                            apiProvider.histogram({})
                        },
                    },                  
                ],
            },                          
            {
                name: 'Filter',
                subMenu: [
                    {
                        name: 'Variant',
                        action: function () {
                            apiProvider.applySimilarVariantFilter( {variantID: params.node.data.variant_id, filterType: 'variant'} )
                        },
                    },  
                    {
                        name: 'Same steps',
                        action: function () {
                            apiProvider.applySimilarVariantFilter( {variantID: params.node.data.variant_id, filterType: 'same_steps'} )
                        },
                    },                  
                    {
                        name: 'Similar steps',
                        action: function () {
                            apiProvider.applySimilarVariantFilter( {variantID: params.node.data.variant_id, filterType: 'similar_steps'} )
                        },
                    },    
                    {
                        name: 'Similar sequence',
                        action: function () {
                            apiProvider.applySimilarVariantFilter( {variantID: params.node.data.variant_id, filterType: 'similar_sequence'} )
                        },
                    },                                     
                ],
            },    


            'separator',
            {
                name: menuLabel('⇩', 'Export'), icon: menuIcon('⇩'),
                subMenu: [
                    {
                        name: menuLabel('CSV', 'CSV Export (all)'), icon: menuIcon('CSV'),
                        action: () => params.api.exportDataAsCsv(
                            {onlySelected: false}
                          ),
                    },
                    {
                        name: menuLabel('CSV', 'CSV Export (selected)'), icon: menuIcon('CSV'),
                        action: () => params.api.exportDataAsCsv(
                            {onlySelected: true}
                          ),
                    },
                    {
                        name: menuLabel('XLS', 'XLS Export (all)'), icon: menuIcon('XLS'),
                        action: () => params.api.exportDataAsExcel(
                            {onlySelected: false}
                          ),
                    },
                    {
                        name: menuLabel('XLS', 'XLS Export (selected)'), icon: menuIcon('XLS'),
                        action: () => params.api.exportDataAsExcel(
                            {onlySelected: true}
                          ),
                    },
                ],
            },        
        ];
        return result;
      }, []);

    const getRowHeight = () => 25;
    
    const onRowClickedFindingContext = (params) => {
        setFindingContextFromGridRow(params?.data || params?.node?.data || {}, 'Explorer - Variants', 'Variant');
    };

    return (
        <Wrapper height={height}>
            <div
                
                style={{
                    height: '100%',
                    width: '100%',
                    fontSize: '12px',
                    fontFamily: 'system-ui'
,
                }}
            >
                <AgGridReact
          onRowClicked={onRowClickedFindingContext} theme={themeAlpine} 
                    rowSelection="multiple"
                    onSelectionChanged={onSelectionChanged}
                    onFirstDataRendered={onFirstDataRendered}
                    getRowHeight={getRowHeight}
                    onColumnVisible={onColumnVisible}
                    onColumnResized={onColumnResized}
                    onSortChanged={onSortChanged}       
                    onFilterChanged={onFilterChanged}
                    getContextMenuItems={getContextMenuItems}
                    suppressScrollOnNewData={true}
                    headerHeight={40}
                    columnDefs={columns}
                    defaultColDef={{
                        sortable: true,
                        filter: true,
                        resizable: true,
                        wrapText: true,
                        autoHeight: true,
                    }}
                    sideBar={
                        {
                            toolPanels: [
                                {
                                    id: 'columns',
                                    labelDefault: 'Columns',
                                    labelKey: 'columns',
                                    iconKey: 'columns',
                                    toolPanel: 'agColumnsToolPanel',
                                    toolPanelParams: {
                                        suppressRowGroups: true,
                                        suppressValues: true,
                                        suppressPivotMode: true, 
                                    }
                      
                                },
                                {
                                    id: 'filters',
                                    labelDefault: 'Filters',
                                    labelKey: 'filters',
                                    iconKey: 'filter',
                                    toolPanel: 'agFiltersToolPanel',
                                },
                            ],
                            hiddenByDefault: false,
                            defaultToolPanel: '',
                        }
                    }
                    animateRows={true}
                    rowData={rows}
                    enableRangeSelection={true}
                    tooltipShowDelay={10}
                    tooltipHideDelay={2000}/>
                    
            </div>
        </Wrapper>
    );
};
